import { useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  Checkbox,
  IconButton,
  List,
  ListItem,
  OutlinedInput,
  Typography,
  styled,
  Stack,
  LinearProgress,
} from "@mui/material";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import RestoreIcon from "@mui/icons-material/Restore";
import RemoveDoneIcon from "@mui/icons-material/RemoveDone";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import { translate } from "../translate2";
import {
  Language,
  Side,
  FocusRequest,
  APILoading,
  createItem,
  ensureIds,
} from "../types";
import { LangChangeContext, useSettings } from "../contexts";

const StrikethroughInput = styled(OutlinedInput)(
  ({ strikethru }: { strikethru: boolean }) => ({
    textDecoration: strikethru ? "line-through" : "none",
  }),
);

export default function CheckboxList() {
  const { langChangeUseEffect, setLangChangeUseEffect } =
    useContext(LangChangeContext);

  const [list, setList] = useState<Language[]>(() => {
    const storedList = localStorage.getItem("user-list");
    if (storedList) {
      return ensureIds(JSON.parse(storedList));
    }
    return [
      createItem({ nativeLang: "Cheese", targetLang: " " }),
      createItem({ nativeLang: "Bread", targetLang: " " }),
      createItem({ nativeLang: "Water", targetLang: " " }),
    ];
  });
  const [clearedList, setClearedList] = useState<Language[]>([]);
  const [showRestoreButton, setShowRestoreButton] = useState<boolean>(false);

  // Always-current snapshot of the list for use inside debounced timers, which
  // fire outside React's render/event flow and can't rely on closure state.
  const listRef = useRef(list);
  listRef.current = list;

  useEffect(() => {
    localStorage.setItem("user-list", JSON.stringify(list));
  }, [list]);

  const [focusRequest, setFocusRequest] = useState<FocusRequest>(null);
  const { settings, setSettings } = useSettings();
  const [isAPILoading, setIsAPILoading] = useState<APILoading>({});

  // Input references for focus(), keyed by item id.
  const inputRefs = useRef<Record<string, HTMLTextAreaElement | null>>({});
  const inputRefs2 = useRef<Record<string, HTMLTextAreaElement | null>>({});
  const timeouts = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Deterministic focus: depends on the whole focus request. The nonce changes
  // on every Enter/Add, so this re-fires even when targeting the same row
  // twice in a row. Clears the request after focusing.
  useEffect(() => {
    if (!focusRequest) return;
    const refs = focusRequest.side === "left" ? inputRefs : inputRefs2;
    refs.current[focusRequest.id]?.querySelector("textarea")?.focus();
    setFocusRequest(null);
  }, [focusRequest]);

  // Clear timeouts on unmount.
  useEffect(() => {
    const map = timeouts.current;
    return () => {
      map.forEach((timeout) => clearTimeout(timeout));
    };
  }, []);

  const handleToggle = (id: string) => () => {
    setList((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, checked: !item.checked } : item,
      ),
    );
  };

  const updateField = (id: string, field: keyof Language, value: string) => {
    setList((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, [field]: value } : item,
      ),
    );
  };

  const addListItem = () => {
    const newItem = createItem();
    setList((prev) => [...prev, newItem]);
    return newItem.id;
  };

  const removeItem = (id: string) => () => {
    setList((prev) => prev.filter((item) => item.id !== id));
  };

  // Monotonic nonce so each focus request is distinct.
  const focusNonce = useRef(0);
  const requestFocus = (id: string, side: Side) => {
    focusNonce.current += 1;
    setFocusRequest({ id, side, nonce: focusNonce.current });
  };

  const handleEnter = (id: string, side: Side) => {
    const newItem = createItem();
    setList((prev) => {
      const index = prev.findIndex((item) => item.id === id);
      const next = [...prev];
      next.splice(index + 1, 0, newItem);
      return next;
    });
    requestFocus(newItem.id, side);
  };

  // Async-safe translation: resolves functionally by id so a late fetch lands
  // on the right row (or is dropped if the row was deleted).
  const translateItem = (id: string, side: Side) => () => {
    setShowRestoreButton(false); // To stop accidental presses

    // Read the current item snapshot from the live ref (this runs inside a
    // debounce timer, outside React's event/render flow).
    const snapshot = listRef.current.find((item) => item.id === id);
    if (!snapshot) return;

    let text: string;
    let fromLang: string;
    let toLang: string;
    let reverseFlag = false;

    if (
      snapshot.targetLang.trim() === "" &&
      snapshot.nativeLang.trim() === ""
    ) {
      return;
    } else if (side === "right" && snapshot.targetLang.trim() !== "") {
      text = snapshot.targetLang.trim();
      fromLang = settings.rightLang;
      toLang = settings.leftLang;
      reverseFlag = true;
    } else if (side === "left" && snapshot.nativeLang.trim() !== "") {
      text = snapshot.nativeLang.trim();
      fromLang = settings.leftLang;
      toLang = settings.rightLang;
    } else {
      return;
    }

    setIsAPILoading((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [side]: true },
    }));

    translate(text, fromLang, toLang)
      .then((result) => {
        setList((prev) =>
          prev.map((item) => {
            if (item.id !== id) return item; // late fetch lands on correct row
            if (result.error) {
              return {
                ...item,
                targetLang: result.text,
                translit: result.translit,
              };
            }
            if (reverseFlag) {
              return { ...item, nativeLang: result.text };
            }
            return {
              ...item,
              targetLang: result.text,
              translit: result.translit,
            };
          }),
        );
      })
      .finally(() => {
        setIsAPILoading((prev) => ({
          ...prev,
          [id]: { ...(prev[id] || {}), [side]: false },
        }));
      });
  };

  const debouncedTranslate = (id: string, side: Side = "left") => {
    if (timeouts.current.has(id)) {
      clearTimeout(timeouts.current.get(id));
    }
    const newTimeout = setTimeout(translateItem(id, side), 1000);
    timeouts.current.set(id, newTimeout);
  };

  // Translate the whole list (used when a language setting changes).
  const translateAll = (side: Side) => {
    list.forEach((item) => debouncedTranslate(item.id, side));
  };

  // Re-translate when the user changes a language setting.
  useEffect(() => {
    if (langChangeUseEffect === false) {
      translateAll("right");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.leftLang]);

  useEffect(() => {
    if (langChangeUseEffect === false) {
      translateAll("left");
    }
    setLangChangeUseEffect(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.rightLang]);

  const clearAll = () => {
    setClearedList([...list]);
    setList([createItem()]);
    setShowRestoreButton(true);
  };

  const clearChecked = () => {
    setClearedList([...list]);
    setList((prev) => prev.filter((item) => !item.checked));
    setShowRestoreButton(true);
  };

  const restoreClearedList = () => {
    setList([...clearedList]);
    setShowRestoreButton(false);
  };

  const toggleDarkMode = () => {
    setSettings({
      ...settings,
      darkMode: settings.darkMode === "light" ? "dark" : "light",
    });
  };

  // First run: translate the example items once.
  useEffect(() => {
    if (settings.firstRun === "true") {
      list.forEach((item) => debouncedTranslate(item.id, "left"));
      setSettings({ ...settings, firstRun: "false" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack display="flex" justifyContent="center" alignItems="center">
      <Stack sx={{ minWidth: "95%", maxWidth: 720 }}>
        <List
          sx={{
            bgcolor:
              settings.darkMode === "light" ? "primary.light" : "primary.main",
            borderRadius: 2,
            boxShadow: 10,
          }}
        >
          {list.map((item) => {
            const labelId = `checkbox-list-label-${item.checked}`;

            return (
              <ListItem key={item.id} disablePadding>
                <ListItem
                  divider={true}
                  sx={{
                    pt: 0,
                    pl: 0,
                    bgcolor: item.checked ? "primary.dark" : "",
                  }}
                  role={undefined}
                  dense
                >
                  <Stack
                    direction={"row"}
                    sx={{
                      width: "100%",
                      maxWidth: 720,
                    }}
                  >
                    <Stack direction={"column"} sx={{ flex: 1 }}>
                      <StrikethroughInput
                        multiline
                        maxRows={2}
                        value={item.nativeLang}
                        size="small"
                        color="error"
                        sx={{
                          pt: 0,
                          pb: 0,
                          flex: 1,
                        }}
                        ref={(el) =>
                          (inputRefs.current[item.id] =
                            el as HTMLTextAreaElement | null)
                        }
                        inputProps={{
                          "aria-labelledby": labelId,
                          maxLength: 40,
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleEnter(item.id, "left");
                          }
                        }}
                        strikethru={item.checked}
                        onChange={(e) => {
                          updateField(item.id, "nativeLang", e.target.value);
                          debouncedTranslate(item.id, "left");
                        }}
                      />
                      <Box height={3} px={1}>
                        {isAPILoading[item.id]?.["right"] && (
                          <LinearProgress
                            color={"secondary"}
                            sx={{ height: 3 }}
                          />
                        )}
                      </Box>
                    </Stack>
                    <Stack direction={"column"} sx={{ flex: 1 }}>
                      <Typography
                        textAlign={"left"}
                        variant="caption"
                        sx={{
                          ml: "1.15em",
                          mb: item.translit !== "" ? "-0.5em" : "0",
                        }}
                      >
                        {item.translit}
                      </Typography>
                      <StrikethroughInput
                        fullWidth
                        multiline
                        maxRows={2}
                        sx={{
                          pt: 0,
                          pb: 0,
                          input: {
                            textAlign: "left",
                          },
                        }}
                        id="result-input"
                        value={item.targetLang}
                        size="small"
                        label={item.translit}
                        ref={(el) =>
                          (inputRefs2.current[item.id] =
                            el as HTMLTextAreaElement | null)
                        }
                        inputProps={{
                          "aria-labelledby": labelId,
                          maxLength: 40,
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleEnter(item.id, "right");
                          }
                        }}
                        strikethru={item.checked}
                        onChange={(e) => {
                          updateField(item.id, "targetLang", e.target.value);
                          debouncedTranslate(item.id, "right");
                        }}
                      />
                      <Box height={3} px={1}>
                        {isAPILoading[item.id]?.["left"] && (
                          <LinearProgress
                            color={"secondary"}
                            sx={{ height: 3 }}
                          />
                        )}
                      </Box>
                    </Stack>
                  </Stack>
                  <Checkbox
                    color="default"
                    checked={item.checked}
                    onClick={handleToggle(item.id)}
                    tabIndex={-1}
                    inputProps={{ "aria-labelledby": labelId }}
                    sx={{ py: 0.5, px: 1 }}
                  />
                  <IconButton
                    edge="end"
                    aria-label="comments"
                    onClick={removeItem(item.id)}
                    sx={{ py: 0.5, px: 1 }}
                  >
                    <RemoveCircleOutlineIcon />
                  </IconButton>
                </ListItem>
              </ListItem>
            );
          })}
        </List>
        <Stack>
          <Stack direction="row" justifyContent="left" p={0.5}>
            <ListItem alignItems="center" disablePadding>
              <IconButton
                onClick={() => {
                  const newId = addListItem();
                  requestFocus(newId, "left");
                }}
              >
                <AddCircleOutlineIcon />
              </IconButton>
              <IconButton onClick={() => clearAll()}>
                <ClearAllIcon />
              </IconButton>
              <IconButton onClick={() => clearChecked()}>
                <RemoveDoneIcon />
              </IconButton>
              <IconButton
                style={{
                  display: showRestoreButton ? "initial" : "none",
                }}
                onClick={() => restoreClearedList()}
              >
                <RestoreIcon />
              </IconButton>
            </ListItem>
            <Stack direction="row" justifyContent="end">
              <IconButton onClick={() => toggleDarkMode()}>
                <Brightness4Icon />
              </IconButton>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}
